import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR, REF_TYPE_LABELS } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";
const dateCls = "h-10 w-44 rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";

export default function Laporan() {
  const { products, warehouses, productMap, warehouseMap } = useMaster();
  const [whFilter, setWhFilter] = useState("");
  const [asOf, setAsOf] = useState("");
  const [stock, setStock] = useState({ rows: [], total_units: 0, total_value: 0 });
  const [ksProduct, setKsProduct] = useState("");
  const [ksWarehouse, setKsWarehouse] = useState("");
  const [kartu, setKartu] = useState(null);
  const [scCustomer, setScCustomer] = useState("");
  const [scFrom, setScFrom] = useState("");
  const [scTo, setScTo] = useState("");
  const [salesCust, setSalesCust] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [smProduct, setSmProduct] = useState("");
  const [smFrom, setSmFrom] = useState(new Date().toISOString().slice(0, 8) + "01");
  const [smTo, setSmTo] = useState(new Date().toISOString().slice(0, 10));
  const [smWh, setSmWh] = useState("");
  const [summary, setSummary] = useState(null);
  const [ksFrom, setKsFrom] = useState("");
  const [ksTo, setKsTo] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (whFilter) params.set("warehouse_id", whFilter);
    if (asOf) params.set("as_of", asOf);
    api.get(`/reports/stock?${params.toString()}`).then((r) => setStock(r.data)).catch(() => {});
  }, [whFilter, asOf]);

  useEffect(() => {
    setKsProduct((v) => v || products[0]?.id || "");
  }, [products]);

  useEffect(() => {
    setKsWarehouse((v) => v || warehouses[0]?.id || "");
  }, [warehouses]);

  useEffect(() => {
    if (!ksProduct || !ksWarehouse) return;
    const params = new URLSearchParams({ product_id: ksProduct, warehouse_id: ksWarehouse });
    if (ksFrom) params.set("date_from", ksFrom);
    if (ksTo) params.set("date_to", ksTo);
    api
      .get(`/reports/kartu-stok?${params.toString()}`)
      .then((r) => setKartu(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [ksProduct, ksWarehouse, ksFrom, ksTo]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (scCustomer) params.set("customer", scCustomer);
    if (scFrom) params.set("date_from", scFrom);
    if (scTo) params.set("date_to", scTo);
    api
      .get(`/reports/sales-by-customer?${params.toString()}`)
      .then((r) => setSalesCust(r.data))
      .catch(() => {});
  }, [scCustomer, scFrom, scTo]);

  useEffect(() => {
    setSmProduct((v) => v || products[0]?.id || "");
  }, [products]);

  useEffect(() => {
    if (!smProduct || !smFrom || !smTo) return;
    const params = new URLSearchParams({ product_id: smProduct, date_from: smFrom, date_to: smTo });
    if (smWh) params.set("warehouse_id", smWh);
    api
      .get(`/reports/stock-summary?${params.toString()}`)
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [smProduct, smFrom, smTo, smWh]);

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const filteredRows = stock.rows.filter((r) => {
    const cat = productMap[r.product_id]?.category || "";
    if (catFilter && cat !== catFilter) return false;
    if (prodSearch) {
      const q = prodSearch.toLowerCase();
      if (!(r.product_name || "").toLowerCase().includes(q) && !(r.sku || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const filteredUnits = filteredRows.reduce((s, r) => s + r.qty, 0);
  const filteredValue = filteredRows.reduce((s, r) => s + r.value, 0);

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Reports</h1>
        <p className="mt-1 text-sm text-zinc-500">FIFO-valued stock positions and per-product stock cards.</p>
      </div>

      <Tabs defaultValue="stok">
        <TabsList className="rounded-sm">
          <TabsTrigger value="stok" data-testid="tab-laporan-stok" className="rounded-sm">Stock Report</TabsTrigger>
          <TabsTrigger value="kartu" data-testid="tab-kartu-stok" className="rounded-sm">Stock Card</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-stock-summary" className="rounded-sm">Monthly Summary</TabsTrigger>
          <TabsTrigger value="customer" data-testid="tab-sales-customer" className="rounded-sm">Sales by Customer</TabsTrigger>
        </TabsList>

        <TabsContent value="stok" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <select data-testid="laporan-warehouse-filter" value={whFilter} onChange={(e) => setWhFilter(e.target.value)} className={`${selectCls} max-w-xs`}>
              <option value="">All Warehouses</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
              ))}
            </select>
            <span className="self-center text-xs font-medium text-zinc-500">Category:</span>
            <select data-testid="laporan-category-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={dateCls}>
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input data-testid="laporan-product-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="Search Product..." className="h-10 w-52 rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
            {(catFilter || prodSearch) && (
              <button type="button" data-testid="laporan-clear-filters" onClick={() => { setCatFilter(""); setProdSearch(""); }} className="rounded-sm border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-600 transition-colors hover:bg-zinc-100">
                Clear Filters
              </button>
            )}
            <span className="self-center text-xs font-medium text-zinc-500">As of:</span>
            <input type="date" data-testid="laporan-as-of" value={asOf} onChange={(e) => setAsOf(e.target.value)} className={dateCls} />
            {asOf && (
              <button type="button" data-testid="laporan-as-of-clear" onClick={() => setAsOf("")} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                Clear date
              </button>
            )}
            {stock.as_of && (
              <span className="self-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="laporan-as-of-label">
                Position as of {stock.as_of}
              </span>
            )}
            <div className="ml-auto flex gap-6 text-sm">
              <p className="text-zinc-500">Total Units: <span className="tnum font-bold text-zinc-900" data-testid="laporan-total-units">{filteredUnits}</span></p>
              <p className="text-zinc-500">FIFO Value: <span className="tnum font-bold text-blue-600" data-testid="laporan-total-value">{formatIDR(filteredValue)}</span></p>
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
            <table className="w-full text-sm" data-testid="laporan-stok-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Warehouse</th>
                  <th className="px-4 py-3 text-right font-medium">Qty</th>
                  <th className="px-4 py-3 text-right font-medium">Avg. Cost</th>
                  <th className="px-4 py-3 text-right font-medium">FIFO Value</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="laporan-stok-empty">
                      No stock available
                    </td>
                  </tr>
                )}
                {filteredRows.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`laporan-stok-row-${r.sku}`}>
                    <td className="px-4 py-2.5 text-zinc-500">
                      <span className="inline-flex rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">{productMap[r.product_id]?.category || "-"}</span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-zinc-900">{r.product_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{r.sku}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{r.warehouse_name}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{r.qty}</td>
                    <td className="tnum px-4 py-2.5 text-right text-zinc-500">{formatIDR(r.avg_cost)}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{formatIDR(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="kartu" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <select data-testid="kartu-product" value={ksProduct} onChange={(e) => setKsProduct(e.target.value)} className={`${selectCls} max-w-xs`}>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select data-testid="kartu-warehouse" value={ksWarehouse} onChange={(e) => setKsWarehouse(e.target.value)} className={`${selectCls} max-w-xs`}>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
              ))}
            </select>
            <span className="self-center text-xs font-medium text-zinc-500">From:</span>
            <input type="date" data-testid="kartu-date-from" value={ksFrom} onChange={(e) => setKsFrom(e.target.value)} className={dateCls} />
            <span className="self-center text-xs font-medium text-zinc-500">To:</span>
            <input type="date" data-testid="kartu-date-to" value={ksTo} onChange={(e) => setKsTo(e.target.value)} className={dateCls} />
            {(ksFrom || ksTo) && (
              <button type="button" data-testid="kartu-date-clear" onClick={() => { setKsFrom(""); setKsTo(""); }} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                Clear
              </button>
            )}
            {kartu && (
              <p className="ml-auto text-sm text-zinc-500">
                Current Stock: <span className="tnum text-lg font-bold text-zinc-900" data-testid="kartu-current-qty">{kartu.current_qty}</span> units
              </p>
            )}
          </div>
          <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
            <table className="w-full text-sm" data-testid="kartu-stok-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Note</th>
                  <th className="px-4 py-3 text-right font-medium">In</th>
                  <th className="px-4 py-3 text-right font-medium">Out</th>
                  <th className="px-4 py-3 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(!kartu || kartu.movements.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="kartu-stok-empty">
                      No movements for this combination yet
                    </td>
                  </tr>
                )}
                {kartu?.movements.map((m) => (
                  <tr key={m.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(m.created_at)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{m.ref_no}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{REF_TYPE_LABELS[m.ref_type] || m.ref_type}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{m.note || "-"}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-emerald-600">{m.direction === "in" ? m.qty : ""}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-red-600">{m.direction === "out" ? m.qty : ""}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{m.balance_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="summary" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <select data-testid="summary-product" value={smProduct} onChange={(e) => setSmProduct(e.target.value)} className={`${selectCls} max-w-xs`}>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{`${p.name} — ${p.sku}`}</option>
              ))}
            </select>
            <span className="self-center text-xs font-medium text-zinc-500">From:</span>
            <input type="date" data-testid="summary-date-from" value={smFrom} onChange={(e) => setSmFrom(e.target.value)} className={dateCls} />
            <span className="self-center text-xs font-medium text-zinc-500">To:</span>
            <input type="date" data-testid="summary-date-to" value={smTo} onChange={(e) => setSmTo(e.target.value)} className={dateCls} />
            <select data-testid="summary-warehouse" value={smWh} onChange={(e) => setSmWh(e.target.value)} className={`${selectCls} max-w-xs`}>
              <option value="">All Warehouses</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
              ))}
            </select>
          </div>

          {summary && (
            <>
              <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
                <table className="w-full text-sm" data-testid="summary-table">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                      <th className="px-4 py-3 font-medium">Warehouse</th>
                      <th className="px-4 py-3 text-right font-medium">Opening</th>
                      <th className="px-4 py-3 text-right font-medium">Purchases</th>
                      <th className="px-4 py-3 text-right font-medium">Transfer In</th>
                      <th className="px-4 py-3 text-right font-medium">Transfer Out</th>
                      <th className="px-4 py-3 text-right font-medium">Sales</th>
                      <th className="px-4 py-3 text-right font-medium">Adjustments</th>
                      <th className="px-4 py-3 text-right font-medium">Closing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.warehouses.map((w) => (
                      <tr key={w.warehouse_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`summary-row-${w.warehouse_code}`}>
                        <td className="px-4 py-2.5 font-medium text-zinc-900">{w.warehouse_name} <span className="text-xs text-zinc-400">({w.warehouse_code})</span></td>
                        <td className="tnum px-4 py-2.5 text-right text-zinc-700">{w.opening}</td>
                        <td className="tnum px-4 py-2.5 text-right font-semibold text-emerald-600">{w.purchase_in || ""}</td>
                        <td className="tnum px-4 py-2.5 text-right text-emerald-600">{w.transfer_in || ""}</td>
                        <td className="tnum px-4 py-2.5 text-right text-red-500">{w.transfer_out || ""}</td>
                        <td className="tnum px-4 py-2.5 text-right text-red-600">{w.sales_out || ""}</td>
                        <td className="tnum px-4 py-2.5 text-right text-zinc-500">{w.adjustments || ""}</td>
                        <td className="tnum px-4 py-2.5 text-right font-bold text-zinc-900">{w.closing}</td>
                      </tr>
                    ))}
                    {summary.warehouses.length > 1 && (
                      <tr className="bg-zinc-50 font-semibold" data-testid="summary-totals-row">
                        <td className="px-4 py-2.5 text-zinc-900">Total</td>
                        <td className="tnum px-4 py-2.5 text-right text-zinc-900">{summary.totals.opening}</td>
                        <td className="tnum px-4 py-2.5 text-right text-emerald-700">{summary.totals.purchase_in}</td>
                        <td className="tnum px-4 py-2.5 text-right text-emerald-700">{summary.totals.transfer_in}</td>
                        <td className="tnum px-4 py-2.5 text-right text-red-500">{summary.totals.transfer_out}</td>
                        <td className="tnum px-4 py-2.5 text-right text-red-600">{summary.totals.sales_out}</td>
                        <td className="tnum px-4 py-2.5 text-right text-zinc-600">{summary.totals.adjustments}</td>
                        <td className="tnum px-4 py-2.5 text-right font-bold text-zinc-900">{summary.totals.closing}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="pt-2 text-lg font-bold tracking-tight text-zinc-900">Sales by Customer — {summary.product.name}</h3>
              <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
                <table className="w-full text-sm" data-testid="summary-customer-table">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Warehouse</th>
                      <th className="px-4 py-3 text-right font-medium">Units Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.warehouses.every((w) => w.sales_by_customer.length === 0) && (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-zinc-400" data-testid="summary-customer-empty">
                          No sales this month
                        </td>
                      </tr>
                    )}
                    {summary.warehouses.flatMap((w) =>
                      w.sales_by_customer.map((c, i) => (
                        <tr key={`${w.warehouse_id}-${i}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`summary-customer-row-${w.warehouse_code}-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-zinc-900">{c.customer_name}</td>
                          <td className="px-4 py-2.5 text-zinc-500">{w.warehouse_name}</td>
                          <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{c.qty}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="customer" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <select data-testid="sales-customer-filter" value={scCustomer} onChange={(e) => setScCustomer(e.target.value)} className={`${selectCls} max-w-xs`}>
              <option value="">All Customers</option>
              {(salesCust?.customers || []).map((c) => (
                <option key={c.customer_name} value={c.customer_name}>{c.customer_name}</option>
              ))}
            </select>
            <input type="date" data-testid="sales-date-from" value={scFrom} onChange={(e) => setScFrom(e.target.value)} className={dateCls} />
            <input type="date" data-testid="sales-date-to" value={scTo} onChange={(e) => setScTo(e.target.value)} className={dateCls} />
            {salesCust && (
              <p className="ml-auto text-sm text-zinc-500">
                Total Sales: <span className="tnum font-bold text-blue-600" data-testid="sales-total">{formatIDR(salesCust.totals.sales)}</span>
                <span className="ml-4">Margin: <span className="tnum font-bold text-emerald-600" data-testid="sales-margin">{formatIDR(salesCust.totals.margin)}</span></span>
              </p>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
            <table className="w-full text-sm" data-testid="sales-customer-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 text-right font-medium">Invoices</th>
                  <th className="px-4 py-3 text-right font-medium">Units</th>
                  <th className="px-4 py-3 text-right font-medium">Sales</th>
                  <th className="px-4 py-3 text-right font-medium">COGS</th>
                  <th className="px-4 py-3 text-right font-medium">Margin</th>
                  <th className="px-4 py-3 text-right font-medium">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {(!salesCust || salesCust.customers.length === 0) && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-zinc-400" data-testid="sales-customer-empty">
                      No sales data for this filter
                    </td>
                  </tr>
                )}
                {salesCust?.customers.map((c, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`sales-customer-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-zinc-900">{c.customer_name}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{c.customer_phone || "-"}</td>
                    <td className="tnum px-4 py-2.5 text-right text-zinc-700">{c.invoice_count}</td>
                    <td className="tnum px-4 py-2.5 text-right text-zinc-700">{c.units}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{formatIDR(c.sales)}</td>
                    <td className="tnum px-4 py-2.5 text-right text-zinc-500">{formatIDR(c.cost)}</td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-emerald-600">{formatIDR(c.margin)}</td>
                    <td className={`tnum px-4 py-2.5 text-right font-semibold ${c.outstanding > 0 ? "text-amber-600" : "text-zinc-300"}`}>{formatIDR(c.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="pt-2 text-lg font-bold tracking-tight text-zinc-900">Invoice Detail</h3>
          <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
            <table className="w-full text-sm" data-testid="sales-detail-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-3 font-medium">Invoice No.</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Items</th>
                  <th className="px-4 py-3 text-right font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(!salesCust || salesCust.invoices.length === 0) && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-zinc-400" data-testid="sales-detail-empty">
                      No invoices for this filter
                    </td>
                  </tr>
                )}
                {salesCust?.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`sales-detail-row-${inv.inv_no}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-600">{inv.inv_no}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(inv.created_at)}</td>
                    <td className="px-4 py-2.5 font-medium text-zinc-900">{inv.customer_name}</td>
                    <td className="px-4 py-2.5 text-zinc-600">
                      {inv.items.map((it) => `${it.product_name} ×${it.qty}`).join(", ")}
                    </td>
                    <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{formatIDR(inv.subtotal)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
